'use client';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { BaseLineCheck3 } from '@/app/components/icons/BaseLineCheck3';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { Loading } from '@/app/components/icons/Loading';
import { ProductLineView } from '@/app/components/icons/ProductLineView';
import HoverPlayMedia from '@/app/components/shared/HoverPlayMedia';
import { Button } from '@/app/components/ui/button';
import { Checkbox } from '@/app/components/ui/checkbox';
import { Dialog, DialogContent } from '@/app/components/ui/dialog';
import { useDevice, useElementErrorMsg, useFetch, useGlobalModal, useResolution } from '@/app/hooks';
import type { CreationWithTask } from '@/app/types';
import { cn, mergeCreationAndTask } from '@/app/utils';
import { materialsGetElementCreations } from '../apis';

interface CreationPostModalProps {
  open?: boolean;
  elementId?: string;
  onClose: (creation?: CreationWithTask) => void;
  disabledIdList?: string[];
}

const COLUMN_COUNT = 4;
const ROW_HEIGHT = 140;
const GAP = 4;
const PAGE_SIZE = 12;

export const CreationPostModal: React.FC<CreationPostModalProps> = ({ open, elementId, onClose, disabledIdList }) => {
  const t = useTranslations();
  const { isMobile } = useDevice();
  const [filterFavor, setFilterFavor] = useState(false);
  const [selectedCreation, setSelectedCreation] = useState<CreationWithTask | undefined>();
  const pageRef = useRef(0);
  const { resolutionMap } = useResolution();
  const [hasMore, setHasMore] = useState(true);
  const parentRef = useRef<HTMLDivElement>(null);
  const [creations, setCreations] = useState<CreationWithTask[]>();
  const { showErrorMsg } = useElementErrorMsg();
  const { open: openSimpleCreationDetailModal } = useGlobalModal('simpleCreationDetailModal');

  const config = isMobile
    ? {
        COLUMN_COUNT: 3,
        ROW_HEIGHT: 124,
        GAP,
      }
    : {
        COLUMN_COUNT,
        ROW_HEIGHT,
        GAP,
      };

  const goToNextPage = () => {
    pageRef.current = pageRef.current + 1;
  };

  const { loading: gettingMyCreationsLoading, runAsync: fetchMoreCreations } = useFetch(
    () =>
      materialsGetElementCreations({
        query: {
          'pager.page': pageRef.current,
          'pager.pagesz': PAGE_SIZE,
          element_id: elementId,
          is_favor: filterFavor,
          types: ['video', 'mini_video'],
        },
      }),
    {
      manual: true,
      onSuccess: (data) => {
        if (data.error) {
          showErrorMsg(data.error);
        } else {
          const newCreations =
            data.data.creations?.map((creation) => {
              return {
                ...mergeCreationAndTask({
                  creation,
                  task: data.data.cid_to_task?.[creation.id],
                }),
                grade: creation.grade,
                id: creation.id,
              };
            }) ?? [];
          const list = (pageRef.current === 0 ? newCreations : creations?.concat(newCreations)) || [];
          setCreations(list);
          const hasMore = list.length < +(data.data.total ?? 0);
          setHasMore(hasMore);
          // 由于这个接口需要关联查询多个task，不可以一次性请求太多数据。为了让弹窗内的数据占满一屏幕，需要手动多请求一次
          if (hasMore && pageRef.current === 0) {
            onLoadMore();
          }
        }
      },
    },
  );

  useEffect(() => {
    if (!open) {
      pageRef.current = 0;
      setCreations([]);
      setHasMore(true);
      setSelectedCreation(undefined);
      setFilterFavor(false);
    } else if (!gettingMyCreationsLoading) {
      fetchMoreCreations();
    }
  }, [open]);

  const onLoadMore = async () => {
    goToNextPage();
    await fetchMoreCreations();
  };

  const handleOpenCreationDetail = (creation: CreationWithTask) => {
    openSimpleCreationDetailModal({
      creation,
    });
  };

  useEffect(() => {
    pageRef.current = 0;
    setSelectedCreation(undefined);
    setCreations([]);
    fetchMoreCreations();
  }, [filterFavor]);

  const renderInspiration = (creation: CreationWithTask) => {
    const active = selectedCreation?.id === creation.id;
    const disabled = disabledIdList?.includes(creation.id);
    return (
      <HoverPlayMedia
        key={creation.id}
        id={creation.id}
        uri={creation.uri}
        coverUri={creation.cover_uri}
        eventName="submit"
        aspectRatio={isMobile ? 1 : undefined}
        onClick={() => !disabled && setSelectedCreation(creation)}
        borderRadiusClassName="rounded-6"
        className={cn(
          'relative aspect-square h-full w-full overflow-hidden rounded-6 before:absolute before:inset-0 before:z-10 before:rounded-6 before:border-2 before:border-transparent before:border-solid before:content-[""] sm:rounded-none',
          { 'before:border-system-blue02': active },
          disabled && 'opacity-25',
        )}
        imageClassName={cn('left-0 right-0 w-full h-full -z-[1] object-cover object-center', active && 'absolute')}
        videoClassName={cn('left-0 right-0 w-full h-full -z-[1] object-cover', active && 'absolute')}
      >
        <div className="absolute right-2 bottom-2 left-2 z-[10] flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 rounded-6 bg-black bg-opacity-[.2] px-2 py-0.5 font-normal text-[12px] text-white leading-[1.4] backdrop-blur-[5px] sm:hidden">
            {creation.task?.settings?.duration ? `${creation.task.settings.duration}s` : '4s'}
            <div className="h-[9px] w-[1px] bg-white bg-opacity-[.16]" />
            {resolutionMap[creation?.resolution?.resolution as keyof typeof resolutionMap]}
          </div>
          <div
            className="rounded-6 bg-black bg-opacity-[.2] px-1 py-0.5 backdrop-blur-[5px]"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenCreationDetail(creation);
            }}
          >
            <ProductLineView className="size-4" />
          </div>
        </div>
        <div className="absolute bottom-0 left-0 h-10 w-full bg-gradient-to-t from-black p-2" />
        <BaseLineCheck3
          className={cn('absolute right-1 bottom-1 z-[1] hidden text-[24px]', {
            inline: active && isMobile,
          })}
        />
      </HoverPlayMedia>
    );
  };

  const rowVirtualizer = useVirtualizer({
    count: Math.ceil((creations?.length ?? 0) / config.COLUMN_COUNT),
    getScrollElement: () => parentRef.current,
    estimateSize: () => config.ROW_HEIGHT + config.GAP,
    overscan: 2,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  const renderInspirationList = () => {
    return (
      <div id="scrollableTarget" ref={parentRef} className="relative h-full overflow-y-auto">
        {!gettingMyCreationsLoading && creations?.length === 0 ? (
          <span
            className={
              'absolute top-[50%] left-[50%] w-full translate-x-[-50%] translate-y-[-50%] px-10 text-center font-medium text-sm leading-[1.4]'
            }
          >
            {filterFavor ? t('popup.dialog.posts.notFavorite') : t('elements.submit.dialog.form.notCreationAvailable')}
          </span>
        ) : (
          <InfiniteScroll
            dataLength={creations?.length ?? 0}
            next={() => onLoadMore()}
            hasMore={hasMore}
            loader={null}
            scrollThreshold={0.5}
            scrollableTarget="scrollableTarget"
          >
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {virtualItems.map((virtualRow) => {
                const rowStartIndex = virtualRow.index * config.COLUMN_COUNT;
                const rowItems = creations?.slice(rowStartIndex, rowStartIndex + config.COLUMN_COUNT);

                return (
                  <div
                    key={virtualRow.key}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${config.ROW_HEIGHT}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                    className={cn('grid gap-1', `grid-cols-${config.COLUMN_COUNT}`)}
                  >
                    {rowItems?.map(renderInspiration)}
                  </div>
                );
              })}
            </div>
          </InfiniteScroll>
        )}

        {gettingMyCreationsLoading && creations?.length === 0 && (
          <div className="absolute top-[50%] left-[50%] max-w-40 translate-x-[-50%] translate-y-[-50%] text-center font-medium text-sm leading-[1.4]">
            <Loading className="animate-spin text-base" />
          </div>
        )}
      </div>
    );
  };

  const Footer = ({ className }: { className?: string }) => {
    return (
      <div className={cn('flex justify-end gap-2', className)}>
        <Button variant="outline" className="border-none px-10 sm:hidden" onClick={() => onClose()}>
          {t('popup.dialog.posts.cancel')}
        </Button>
        <Button
          variant="primary"
          className="border-none px-10 sm:flex-1"
          disabled={!selectedCreation}
          onClick={() => onClose(selectedCreation)}
        >
          {t('elements.elementDialog.confirm')}
        </Button>
      </div>
    );
  };

  const FavoriteBtn = ({ className }: { className?: string }) => {
    return (
      <div className={cn('flex items-center gap-1', className)}>
        <Checkbox
          className="text-system-white64"
          id="favorite"
          checked={filterFavor}
          onClick={() => {
            setFilterFavor(!filterFavor);
          }}
        />
        <label className="cursor-pointer font-normal text-sm text-white/80 leading-[1.4]" htmlFor="favorite">
          {t('elements.submit.dialog.form.favor')}
        </label>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent
        className="max-w-[620px] gap-0 p-6 sm:h-full sm:w-full sm:max-w-full sm:space-y-0 sm:rounded-none sm:border-none sm:bg-black sm:px-0 sm:pt-4 sm:pb-0"
        closable={!isMobile}
      >
        <h3 className="relative justify-center font-medium text-[20px] text-white leading-[1.4] sm:absolute sm:top-4 sm:right-0 sm:left-0 sm:flex sm:h-11 sm:items-center sm:text-center">
          <Button
            className={cn('absolute top-0.5 left-2 z-10 p-0', isMobile ? 'block' : 'hidden')}
            onClick={() => {
              onClose();
            }}
          >
            <BaseLineLeft className="size-6" />
          </Button>
          {t('elements.submit.dialog.form.video')}
          <FavoriteBtn className="absolute top-0 right-2 bottom-0 hidden sm:flex" />
        </h3>
        <div className={cn('my-4 flex h-[428px] flex-col overflow-hidden sm:h-[unset] sm:pt-[60px] sm:pb-[60px]')}>
          {renderInspirationList()}
        </div>
        <div className="flex items-center justify-between gap-6 sm:hidden">
          <FavoriteBtn />
          <Footer />
        </div>
        <div className="absolute right-0 bottom-0 left-0 hidden bg-system-black64 p-4 sm:block">
          <Footer className="" />
        </div>
      </DialogContent>
    </Dialog>
  );
};
